import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useGlobalContext } from 'GlobalContext';
import Dropdown from 'components/forms/basefields/Dropdown';
import { EmailDomain, EmailMessage, EmailTemplate } from './EmailTypes';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import TextInput from 'components/forms/basefields/TextInput';
import TextareaInput from 'components/forms/basefields/TextareaInput';
import { sanitizeTextareaFromString } from 'services/utils/convertTextareaData';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import AttachmentSelect from 'components/forms/basefields/AttachmentSelect';
import { acceptedFileTypes } from '../../views/attachment/AttachmentTypes';
import EmailRecipientInput from './EmailRecipientInput';
import { EmailRecipientOption, emailStringToRecipients, getEmailTemplateRecipients, recipientsToEmailString } from './getEmailTemplateRecipients';
import { resolveTemplateTags } from '../settings/email/templates/resolveTemplateTags';
import { useSettings } from 'services/settings/SettingsContext';
import 'react-quill/dist/quill.snow.css';
import '../../style/scss/forms.scss';

interface EmailFormFieldsProps {
    initialData: EmailMessage;
    placement: 'inline' | 'modal';
    emailTemplates: EmailTemplate[];
    customDomains: EmailDomain[];
    connectedFeature?: string;
    objectData?: any;
    buttonLoader: boolean;
    handleClose: () => void;
    handleDelete: () => void;
    handleSubmit: () => void;
}

const EmailFormFields: React.FC<EmailFormFieldsProps> = ({ 
    initialData, placement, emailTemplates, customDomains, connectedFeature, objectData, buttonLoader, 
    handleClose, handleDelete, handleSubmit
}) => {
    const { t } = useTranslation();
    const { allowedRights } = useAuthContext();
    const { errorMessages } = useGlobalContext();
    const { closeAllModals } = useModal();
    const { environmentSettings, userPreferences } = useSettings();
    const { setUpdatedData, updatedData } = useContext(FormFieldContext);
    const [toRecipients, setToRecipients] = useState<EmailRecipientOption[]>(emailStringToRecipients(initialData.to))
    const [showExtraFields, setShowExtraFields] = useState<boolean>(false);
    const history = useHistory();

    // Get the value from the updated data, or fallback to the initial data
    const getValue = (field: keyof EmailMessage) => {
        if (Object.prototype.hasOwnProperty.call(updatedData, field)) {
            return updatedData[field] ?? null;
        }
        return initialData[field];
    };

    // Get the selected option from the updated data, or fallback to the initial data
    const getSelectedOption = <T extends { id?: number }>( field: keyof EmailMessage, options: T[]) => {
        const value = field in updatedData ? updatedData[field] : initialData[field];
        if (value == null) return null;
        return options.find(option => option.id === value) ?? null;
    };

    // Get the selected template and its custom domain from email
    const selectedTemplateId = getValue('template');
    const selectedTemplate = emailTemplates.find(email_template => email_template.id === selectedTemplateId);
    const selectedTemplateHasCustomDomain = !!selectedTemplate?.custom_domain;
    const displayedFromEmail = selectedTemplateHasCustomDomain ? selectedTemplate?.from_email : getValue('from_email');

    // Handle input change
    const handleInputChange = (value: string, fieldName: 'subject' | 'to' | 'bcc' | 'from_email') => {
        setUpdatedData((prev: EmailMessage) => ({ ...prev, [fieldName]: value }));
    };

    // Handle recipients change
    const handleRecipientsChange = (recipients: EmailRecipientOption[]) => {
        setToRecipients(recipients)
        setUpdatedData((prev: EmailMessage) => ({
            ...prev,
            to: recipientsToEmailString(recipients)
        }))
    }

    // Handle textarea change
    const handleTextareaChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, fieldName: 'body') => {
        // Get the actual value
        const actualValue = typeof value === 'string' ? value : value.target.value;

        // Sanitize the textarea value
        const sanitizedValue = sanitizeTextareaFromString(actualValue);

        // Update the data and updated data
        setUpdatedData((prev: EmailMessage) => ({ ...prev, [fieldName]: sanitizedValue }));
    };

    // Handle the task type change
    const handleTemplateChange = (selectedValue: string) => {

        // Handle unselection of a template
        if (selectedValue === 'none') {

            // Set recipients to empty
            setToRecipients([])

            // Set empty updated data
            setUpdatedData((prev: EmailMessage) => ({
                ...prev,
                template: null,
                bcc: null,
                subject: '',
                body: '',
                attachments: [],
                add_object_pdf: false
            }));

            return;
        }

        // Get the new email template object
        const newEmailTemplateId = parseInt(selectedValue);
        const newEmailTemplate = emailTemplates.find(template => template.id === newEmailTemplateId);

        if (newEmailTemplate) {
            // Get the recipients from the email template
            const recipients = getEmailTemplateRecipients({ template: newEmailTemplate, objectData });

            // Use the templates own connected feature for tag resolution so that
            // e.g. an appointment template selected in a job context resolves appointment tags
            const effectiveFeature = newEmailTemplate.connected_feature || connectedFeature;

            // Resolve tags in the subject
            const resolvedSubject = resolveTemplateTags({
                text: newEmailTemplate.subject ?? '',
                connectedFeature: effectiveFeature,
                objectData,
                currentUser: {
                    first_name: userPreferences.first_name,
                    last_name: userPreferences.last_name
                },
                locale: environmentSettings?.default_locale,
                language: environmentSettings?.default_language,
                currency: environmentSettings?.default_currency ?? 'EUR',
                multilineSeparator: ', '
            })

            // Resolve tags in the body
            const resolvedBody = resolveTemplateTags({
                text: newEmailTemplate.body ?? '',
                connectedFeature: effectiveFeature,
                objectData,
                currentUser: {
                    first_name: userPreferences.first_name,
                    last_name: userPreferences.last_name
                },
                locale: environmentSettings?.default_locale,
                language: environmentSettings?.default_language,
                currency: environmentSettings?.default_currency ?? 'EUR',
                multilineSeparator: '<br>'
            })

            // Set the recipients
            setToRecipients(recipients)

            // Update the updated data
            setUpdatedData((prev: EmailMessage) => ({ 
                ...prev, 
                template: newEmailTemplateId,
                to: recipientsToEmailString(recipients),
                bcc: newEmailTemplate.default_bcc_recipient ? newEmailTemplate.default_bcc_recipient : null,
                custom_domain: newEmailTemplate.custom_domain ?? null,
                from_email: newEmailTemplate.from_email,
                subject: resolvedSubject,
                body: resolvedBody,
                attachments: newEmailTemplate.default_attachments,
                add_object_pdf: newEmailTemplate.add_object_pdf
            }));
        }
    };

    // Handle sender email change
    const handleSenderEmailChange = (selectedValue: number) => {
        // Get the selected custom domain
        const selectedDomain = customDomains.find(domain => domain.id === selectedValue);
        
        if (selectedDomain) {
            // Configure the sender details
            const from_email = `${selectedDomain.default_from_prefix}@${selectedDomain.domain}`;
            const from_name = selectedDomain.division_name;
            const custom_domain = selectedDomain.id;

            // Update the updated data
            setUpdatedData((prev: EmailMessage) => ({ 
                ...prev, 
                custom_domain, 
                from_email,
                from_name
            }))
        }        
    };

    // Handle redirect to email template settings
    const handleRedirectToEmailTemplateSettings = () => {

        // If the task form is opened within a modal, close all modals
        if (placement === 'modal') {
            closeAllModals();
        }

        // Redirect to the url
        history.push('/settings/features/email');
    };

    return (
        <div className='email-form'>
            <div>
                <div className='label-link-wrapper'>
                    <label>
                        {t('email.general.template_label')} 
                    </label>
                    {allowedRights.includes('change_settings') && (
                        <span className='text-link'
                              onClick={handleRedirectToEmailTemplateSettings}>
                            {t('email.general.modify_templates_label')}
                            <FontAwesomeIcon 
                                icon={faWrench} 
                                className='text-link-icon'
                            />
                        </span>
                    )}
                </div>
                <Dropdown<EmailTemplate>
                    options={emailTemplates}
                    id='template'
                    name='template'
                    disabled_selected={t('email.general.select_template_disabled_selected')}
                    selectedOption={getSelectedOption('template', emailTemplates)}
                    onChange={(value) => handleTemplateChange(value)}
                    selectionFormat={(option) => `${option.name}`}
                    optionFormat='name'
                    showSearch={false}
                    allowNoneOption={true}
                />
            </div>
            <div>
                <label>
                    {t('email.general.recipients_label')}
                </label>
                <div className='recipient-input-wrapper'>
                    <EmailRecipientInput
                        recipients={toRecipients}
                        placeholder={t('email.general.recipients_placeholder')}
                        isInvalid={errorMessages['to'] || errorMessages['recipients']}
                        onChange={handleRecipientsChange}
                    />
                    <span className='text-link'
                        onClick={() => setShowExtraFields(!showExtraFields)}>
                        {!showExtraFields 
                            ? t('email.general.show_bcc_from_email_fields')
                            : t('email.general.hide_bcc_from_email_fields')
                        }
                    </span>
                </div>
                {errorMessages['recipients'] && (
                    <div className='error-message'>
                        {t(errorMessages['recipients'])}
                    </div>
                )}
            </div>
            {showExtraFields && (
                <>
                    <div>
                        <label>
                            {t('email.general.bcc_label')}
                        </label>
                        <TextInput
                            name='bcc'
                            value={getValue('bcc')}
                            placeholder='email.general.bcc_placeholder'
                            onChange={(value) => handleInputChange(value, 'bcc')}
                            isInvalid={errorMessages['bcc']}
                        />
                        {errorMessages['bcc'] && (
                            <div className='error-message'>
                                {t(errorMessages['bcc'])}
                            </div>
                        )}
                    </div>
                    <div>
                        <label>
                            {t('email.general.from_email')}
                        </label>
                        {selectedTemplateHasCustomDomain ? (
                            <>
                                <div className='fake-input'>
                                    {displayedFromEmail}
                                </div>
                            </>
                        ) : customDomains.length === 0 ? (
                            <div className='fake-input'>
                                {displayedFromEmail}
                            </div>
                        ) : (
                            <>
                                <Dropdown<EmailDomain>
                                    options={customDomains}
                                    id='email_domain'
                                    name='email_domain'
                                    disabled_selected={t('email.general.email_domain_disabled_selected')}
                                    selectedOption={getSelectedOption('custom_domain', customDomains)}
                                    onChange={(selectedValue) => handleSenderEmailChange(parseInt(selectedValue))}
                                    selectionFormat={(option) => `${option.default_from_prefix}@${option.domain}`}
                                    optionFormat={(option) => `${option.default_from_prefix}@${option.domain}`}
                                    disabled={customDomains.length === 1}
                                    isInvalid={errorMessages['from_email']}
                                    showSearch={false}
                                    allowNoneOption={false}
                                />
                                {errorMessages['from_email'] && (
                                    <div className='error-message'>
                                        {t(errorMessages['from_email'])}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}
            <div>
                <label>
                    {t('email.general.subject_label')}
                </label>
                <TextInput
                    name='subject'
                    value={getValue('subject')}
                    placeholder='email.general.subject_placeholder'
                    onChange={(value) => handleInputChange(value, 'subject')}
                    isInvalid={errorMessages['subject']}
                />
                {errorMessages['subject'] && (
                    <div className='error-message'>
                        {t(errorMessages['subject'])}
                    </div>
                )}
            </div>
            <div className='edit-mode'>
                <label>
                    {t('email.general.body_label')}
                </label>
                <TextareaInput
                    name='body'
                    value={getValue('body')}
                    placeholder='email.general.body_placeholder'
                    rows={8}
                    enableStyling='simple'
                    showToolbarInitially={true}
                    onChange={(value) => handleTextareaChange(value, 'body')}
                />
                {errorMessages['body'] && (
                    <div className='error-message'>
                        {t(errorMessages['body'])}
                    </div>
                )}
            </div>
            <div>
                <label>
                    {t('email.general.attachments_label')}
                </label>
                <AttachmentSelect
                    key={`email-attachments-${getValue('template') ?? 'none'}`}
                    attachments={getValue('attachments') ?? []}
                    addObjectPdf={connectedFeature && connectedFeature !== 'appointments'
                        ? { 
                            shouldAddObjectPdf: true,
                            connectedFeature: connectedFeature,
                            addObjectPdfValue: getValue('add_object_pdf') ?? false
                         }
                        : undefined
                    }
                    acceptedFileTypes={acceptedFileTypes}
                    mode='email_form'
                    onChange={({ attachments, newAttachments, addObjectPdfValue }) => {
                        setUpdatedData((prev: EmailMessage) => ({
                            ...prev,
                            attachments: attachments,
                            new_attachments: newAttachments,
                            add_object_pdf: addObjectPdfValue
                        }))
                    }}
                />
            </div>
            <div className='footer'>
                <div className='left-content'>
                    {placement === 'modal' && initialData.id && (
                        <SecondaryButton
                            onClick={handleDelete} 
                            label="email.general.delete_email_label"
                            size='extra-small'
                            customClass='danger'
                        />
                    )}
                </div>
                <div className='right-content'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label="general.cancel"
                        size={placement === 'inline' ? 'extra-small' : 'small'}
                    />
                    <PrimaryButton
                        onClick={handleSubmit}
                        label="email.general.send_email"
                        size={placement === 'inline' ? 'extra-small' : 'small'}
                        loading={buttonLoader}
                    />
                </div>
            </div>
        </div>
    );
};

export default EmailFormFields;